import { Cookies } from "react-cookie";

import { STORE_INFO_MAPPING } from "./mapping";
import { CountryCode, Language, StoreId } from "@ubnt/store.core/lib/types";
import { DEFAULT_LANGUAGE, GLOBAL_SITE_CONFIG } from "./constants";

const cookies = new Cookies();

/**
 * Util to get language for given country code
 */
export const getDefaultLanguageByRegion = (
  countryCode: CountryCode,
): Language => {
  const storeId = STORE_INFO_MAPPING.find((store) =>
    store.countries.includes(countryCode),
  )?.id;

  if (!storeId) {
    return DEFAULT_LANGUAGE;
  }

  return (
    GLOBAL_SITE_CONFIG.ui.regions[storeId].defaultLanguage ?? DEFAULT_LANGUAGE
  );
};

/**
 * DEPRECATED LANGUAGES
 */
export enum DEPRECATED_LANGUAGES {
  ES = "es",
  PT = "pt",
}

/**
 * Handle deprecated specifically localized languages
 * Resolves es/pt to es-es/es-mx/pt-pt/pt-br based on storeId
 */
export const resolveDeprecatedLang = (
  lang: string,
  storeId: StoreId,
): Language | null => {
  if (lang === DEPRECATED_LANGUAGES.ES) {
    return storeId === StoreId.Eu ? Language.EsEs : Language.EsMx;
  }
  if (lang === DEPRECATED_LANGUAGES.PT) {
    return storeId === StoreId.Eu ? Language.PtPt : Language.PtBr;
  }
  return null;
};

/**
 * Eval country code
 */
export const evalCountryCode = (
  countryCode: string | undefined,
): CountryCode | undefined => {
  return Object.values(CountryCode).find(
    (cc) => cc.toUpperCase() === countryCode?.toUpperCase(),
  );
};

/**
 * Parse language code
 */
export const evalLanguageCode = (
  languageCode: string | undefined,
): Language | undefined => {
  return Object.values(Language).find(
    (lc) => lc.toUpperCase() === languageCode?.toUpperCase(),
  );
};

/**
 * Parse StoreId
 */
export const evalStoreId = (
  storeId: string | undefined,
): StoreId | undefined => {
  return Object.values(StoreId).find(
    (gsi) => gsi.toUpperCase() === storeId?.toUpperCase(),
  );
};

export const isValidStoreLanguage = (storeId: StoreId, language: Language) => {
  const storeLanguages = GLOBAL_SITE_CONFIG.ui.regions[storeId].languages;
  return storeLanguages.includes(language);
};

// DYNAMIC PATH HELPERS
export const BASE_PATH = "";
export const STORE_ID_PARAM = ":storeId";
export const LANGUAGE_PARAM = ":language";
export const PATH_PARAM = ":path";
export const NOT_FOUND_PATH = "/404";

/**
 * This returns all page base paths for a given page path.
 * Note: React Router v6 doesn't support regex patterns in route paths, so we use
 * simple :storeId and :language parameters. Validation happens in components.
 */
const REGIONAL_PREFIXES = [
  BASE_PATH,
  STORE_ID_PARAM, // Simple pattern: :storeId
  `${STORE_ID_PARAM}/${LANGUAGE_PARAM}`, // Simple pattern: :storeId/:language
];

export const getPageBasePaths = (pagePath: string) => {
  const base = pagePath.replace(/\/$/, BASE_PATH); // remove trailing slash

  return REGIONAL_PREFIXES.map((path) => {
    if (!path) {
      return `/${base.replace(/^\//, BASE_PATH)}`;
    }

    return `/${path}/${base.replace(/^\//, BASE_PATH)}`;
  });
};

export const saveCookie = (codeKey, language) => {
  const today = new Date();
  cookies.set(codeKey, language, {
    path: "/",
    domain: __CONFIG__.isProd ? ".ui.com" : BASE_PATH,
    secure: __CONFIG__.isProd || __CONFIG__.isQA,
    sameSite: "lax",
    expires: new Date(today.setFullYear(today.getFullYear() + 1)), // add one year to current date
  });
};
